import React, { useEffect, useState } from 'react';
import { all, equals } from 'ramda';
import { arrayOf, bool, func, oneOfType, shape, string } from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';

import { useGetGlobalCustomersQuery } from 'feature/subscription-plans/enrollment/enrollmentEndpoints';

import { SORT_BY, SORT_BY_TOOLTIP } from './terminalLocatorConstants';

const styles = {
  drawerFooter: {
    alignItems: 'center',
    backgroundColor: 'surface.white',
    borderTop: '1px solid',
    borderTopColor: 'graphic.light',
    bottom: 0,
    boxShadow: 0,
    display: 'flex',
    gridGap: 2,
    height: 68,
    justifyContent: 'space-around',
    position: 'sticky',
    px: 2,
    py: 3,
    zIndex: 1,
  },
  drawerHeader: {
    backgroundColor: 'surface.white',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  mobileDivider: {
    mb: 1,
    ml: 3,
    mr: 3.625,
    mt: 3,
  },
  mobileDrawer: {
    width: '100%',
  },
};

const TerminalLocatorMobileDrawer = ({
  ClearFilters,
  FilterAdditives,
  FilterProducts,
  additivesHook: [additives, setAdditives],
  allAdditives,
  allProducts,
  productsHook: [products, setProducts],
  location,
  mobileDrawerHook: [open, setOpen],
  sortHook: [sortBy, setSortBy],
}) => {
  const { data: globalCustomersQuery = [] } = useGetGlobalCustomersQuery();
  const hasAffiliateWithSubscription = globalCustomersQuery.some(affiliate =>
    equals(affiliate.hasActiveSubscription, true)
  );

  const [unsavedSortBy, setUnsavedSortBy] = useState(sortBy);
  const [unsavedProducts, setUnsavedProducts] = useState([...products]);
  const [unsavedAdditives, setUnsavedAdditives] = useState([...additives]);

  const [rwtTooltipOpen, setRwtTooltipOpen] = useState(false);

  const rwtTooltipMessage = hasAffiliateWithSubscription
    ? SORT_BY_TOOLTIP.WAIT_TIMES_RESTRICTION
    : SORT_BY_TOOLTIP.SWIFT_PLUS_REQUIRED;

  useEffect(() => {
    if (rwtTooltipOpen) {
      setTimeout(() => {
        setRwtTooltipOpen(false);
      }, 3000);
    }
  }, [rwtTooltipOpen]);

  useEffect(() => {
    setUnsavedSortBy(sortBy);
    setUnsavedProducts([...products]);
    setUnsavedAdditives([...additives]);
  }, [sortBy, additives, products, open]);

  const handleChangeProducts = (event, value) => {
    const isSelectAll = s => s.productIdName === 'Select All';
    const stateHasSelectAll = unsavedProducts.some(isSelectAll);
    const selectAll =
      !stateHasSelectAll && (value.some(isSelectAll) || value.length === allProducts.length - 1);

    selectAll
      ? setUnsavedProducts(allProducts)
      : stateHasSelectAll
      ? !value.some(isSelectAll)
        ? setUnsavedProducts(value.filter(isSelectAll))
        : setUnsavedProducts(value.filter(product => product.productIdName !== 'Select All'))
      : setUnsavedProducts(value);
  };

  const handleChangeAdditives = (event, value) => {
    const isSelectAll = s => s.additiveName === 'Select All';
    const stateHasSelectAll = unsavedAdditives.some(isSelectAll);
    const selectAll =
      !stateHasSelectAll && (value.some(isSelectAll) || value.length === allAdditives.length - 1);

    selectAll
      ? setUnsavedAdditives(allAdditives)
      : stateHasSelectAll
      ? !value.some(isSelectAll)
        ? setUnsavedAdditives(value.filter(isSelectAll))
        : setUnsavedAdditives(value.filter(additive => additive.additiveName !== 'Select All'))
      : setUnsavedAdditives(value);
  };

  return (
    <Drawer
      anchor="right"
      onClose={() => setOpen()}
      open={open}
      slotProps={{
        paper: {
          sx: styles.mobileDrawer,
        },
      }}
    >
      <Box sx={styles.drawerHeader}>
        <IconButton onClick={() => setOpen()} sx={{ mx: 0.25, my: 0, p: 1 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ mx: 3 }}>
        <Typography variant="h6">Sort by</Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <RadioGroup onChange={(event, value) => setUnsavedSortBy(value)} value={unsavedSortBy}>
            <FormControlLabel
              control={<Radio />}
              disabled={!location}
              label={SORT_BY.DISTANCE.label}
              value={SORT_BY.DISTANCE.value}
            />
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
              }}
            >
              <FormControlLabel
                control={<Radio />}
                disabled={!location || !hasAffiliateWithSubscription}
                label={SORT_BY.WAIT_TIMES.label}
                sx={{ mr: 0.25 }}
                value={SORT_BY.WAIT_TIMES.value}
              />
              <ClickAwayListener onClickAway={() => setRwtTooltipOpen(false)}>
                <Tooltip
                  open={rwtTooltipOpen}
                  title={
                    <Box sx={{ pointerEvents: 'auto', textAlign: 'center' }}>
                      {rwtTooltipMessage}
                    </Box>
                  }
                >
                  <IconButton onClick={() => setRwtTooltipOpen(true)}>
                    <InfoIcon sx={{ color: 'gray.dark' }} />
                  </IconButton>
                </Tooltip>
              </ClickAwayListener>
            </Box>
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider sx={styles.mobileDivider} />
      <Box sx={{ mx: 3, my: 1 }}>
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Filter by</Typography>
          <ClearFilters
            onClick={() => {
              setUnsavedProducts([]);
              setUnsavedAdditives([]);
            }}
          />
        </Box>
        <FilterProducts
          collapsible
          collapsibleLabel="Base Product"
          label="Search Products"
          onChange={handleChangeProducts}
          open
          value={unsavedProducts}
        />
        <Divider sx={styles.mobileDivider} />
        <FilterAdditives
          collapsible
          collapsibleLabel="Additive"
          label="Search Additives"
          onChange={handleChangeAdditives}
          open
          value={unsavedAdditives}
        />
      </Box>
      <Box sx={{ flex: 1 }} />
      <Box sx={styles.drawerFooter}>
        <Button onClick={() => setOpen()} variant="outlined">
          Cancel
        </Button>
        <Button
          disabled={all(equals(true), [
            equals(unsavedSortBy, sortBy),
            equals(unsavedProducts, products),
            equals(unsavedAdditives, additives),
          ])}
          onClick={() => {
            setSortBy(unsavedSortBy);
            setProducts(unsavedProducts);
            setAdditives(unsavedAdditives);
            setOpen();
          }}
        >
          Apply
        </Button>
      </Box>
    </Drawer>
  );
};
TerminalLocatorMobileDrawer.propTypes = {
  ClearFilters: func.isRequired,
  FilterAdditives: func.isRequired,
  FilterProducts: func.isRequired,
  additivesHook: arrayOf(oneOfType([arrayOf(shape({})), func])).isRequired,
  allAdditives: arrayOf(shape({})).isRequired,
  allProducts: arrayOf(shape({})).isRequired,
  location: shape({}),
  mobileDrawerHook: arrayOf(oneOfType([bool, func])).isRequired,
  productsHook: arrayOf(oneOfType([arrayOf(shape({})), func])).isRequired,
  sortHook: arrayOf(oneOfType([string, func])).isRequired,
};

TerminalLocatorMobileDrawer.defaultProps = {
  location: null,
};

export default TerminalLocatorMobileDrawer;
